import React, { Suspense, useEffect, useState } from "react";
import {
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Box,
} from "@mui/material";
import styles from "./inventory.module.scss";
import {
  mdiChevronRight,
  mdiDotsVertical,
  mdiCheck,
  mdiLockOutline,
  mdiAlertCircleOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { inventoryService, tagService } from "../../api/services";
import {
  CreateCopyInventoryDialog,
  InventoryDetails,
} from "./../../containers";
import { EmptyDashboard, ConfirmationDialog, Loader, CustomTooltip } from "../../components";
import { useClient } from "../../utils/ClientContext";
import { inventoryUtils } from "../../utils";
import { IDialogConfig, IAdminException, IAdminExceptionProps } from "../../interfaces";
import { useNavigate } from "react-router-dom";
import { AdminExceptionHelper, useException } from "../../utils";


const Inventory: React.FC = () => {
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState<"success" | "error">("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [activeInventory, setActiveInventory] = useState<any>(null);
  const [recentlyLockedInventory, setRecentlyLockedInventory] =
    useState<any>(null);
  const [inventoryId, setInventoryId] = useState<number | null>(null);
  const [emptyState, setEmptyState] = useState(true);
  const [isTagCompleted, setIsTagCompleted] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const userId = Number(localStorage.getItem("userId"));
  const [inventoryMenuEl, setInventoryMenuEl] = useState<null | HTMLElement>(
    null
  );const navigate = useNavigate();
  const [subMenuInventoryEl, setSubMenuInventoryEl] =
    useState<null | HTMLElement>(null);
  const [createCopyInventoryOpen, setCreateCopyInventoryOpen] = useState(false);
   const [dialogConfig, setDialogConfig] = useState<IDialogConfig>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {}, 
  });
  const [hasInventorySession, setHasInventorySession] = React.useState(false);
  const [userName, setUserName] = useState("");
  const { client } = useClient();
  const clientId = client?.id;
  const [reloadInventoryData, setReloadInventoryData] = useState<Boolean>(false);
  const [showLoader, setShowLoader] = useState(false);
  const [adminExceptionProps, setAdminExceptionProps] = useState<IAdminExceptionProps>();
  const [adminException, setAdminException] = useState<IAdminException>();
  const { setException, clearException } = useException();
    const [byPassDialogOpen, setByPassDialogOpen] = useState(false);
    const [isByPassConfirm, setIsByPassConfirm] = useState(false);
  // Dynamically assign custom icons based on severity
  const getIcon = () => (
    <Icon
      path={severity === "success" ? mdiCheck : mdiAlertCircleOutline}
      size={1}
    />
  );

  // Close Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  useEffect(() => {
    let unAcceptedExc = AdminExceptionHelper.adminException(adminExceptionProps, true);
    let exc = AdminExceptionHelper.adminException(adminExceptionProps, false);
    setAdminException(exc);
    if(unAcceptedExc && unAcceptedExc.isException){
      setException({
        title: unAcceptedExc.bannnerTitle,
        message: unAcceptedExc.bannerMessage
      });
    }
    else{
      clearException();
    }
  }, [adminExceptionProps]);
  const handleDialogSuccess = (isSuccess: boolean, message: string) => {
    if (isSuccess) {
      setEmptyState(true);
      setSeverity("success");
      setSnackbarMessage(message);
      setOpenSnackbar(true);
      setTimeout(() => {
        setOpenSnackbar(false);
      }, 3000);
      setReloadInventoryData((prev) => !prev);
    }
    setCreateCopyInventoryOpen(false);
  };

  // Fetch Active Inventory
  const fetchActiveInventory = async () => {
    try {setShowLoader(true);
      const data = await inventoryService.getActiveInventorySessionAsync();
      if (data?.inventory && data?.inventory.inventoryId !== 0) {
        setActiveInventory(data.inventory);
        localStorage.setItem("inventoryId", data.inventory.inventoryId);
        setInventoryId(data.inventory.inventoryId);
        setIsTagCompleted(data.inventory.isTagCompleted);
        setAdminExceptionProps({
          exceptions: data.inventory.exceptions,
          unacceptedExceptions: data.inventory.unacceptedExceptions
        });
        setIsLocked(false);
        setEmptyState(false);
        setHasInventorySession(true);
      } else {
        fetchRecentlyLockedInventory();
      }
    } catch (error) {
      console.error("Failed to fetch active inventory session:", error);
      setEmptyState(true);
    }finally {
      setShowLoader(false);
    }
  };

  // Fetch Recently Locked Inventory
  const fetchRecentlyLockedInventory = async () => {
    try {setShowLoader(true);
      const data =
        await inventoryService.getRecentlyLockedInventorySessionAsync();
      if (data?.inventory) {
        setActiveInventory(data.inventory);
        localStorage.setItem("inventoryId", data.inventory.inventoryId);
        setIsLocked(data.inventory.isLocked);
        setAdminExceptionProps({
          exceptions: data.inventory.exceptions,
          unacceptedExceptions: data.inventory.unacceptedExceptions
        });
        setInventoryId(data.inventory.inventoryId);
        setIsTagCompleted(data.inventory.isTagCompleted);
        setHasInventorySession(true);
        setEmptyState(false);
      } else {
        setHasInventorySession(false);
        setEmptyState(true);
      }
    } catch (error) {
      setEmptyState(true);
      console.error(
        "Failed to fetch recently locked inventory session:",
        error
      );
    }finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    clearException();
    if (clientId) {
      setEmptyState(true);
      fetchActiveInventory();
    }
    const storedUserDetails = localStorage.getItem("UserDetails");
    if (storedUserDetails) {
      const userDetails = JSON.parse(storedUserDetails);
      if (userDetails && Array.isArray(userDetails.roles)) {
        setUserName(userDetails.firstName + " " + userDetails.lastName);
      }
    }
  }, [clientId, reloadInventoryData]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setInventoryMenuEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setInventoryMenuEl(null);
    setSubMenuInventoryEl(null);
  };

  const handleNewSession = () => {
    setCreateCopyInventoryOpen(true);
    handleMenuClose();
  };
  const toggleLoader = (loading: boolean) => {
    setShowLoader(loading);
  };
  const onCreateCopyInventoryClose = () => {
    setCreateCopyInventoryOpen(false);
    handleMenuClose();
  };

  // Lock Inventory
  const handleLockInventory = async () => {
    if (!activeInventory || !inventoryId) return;
     if(requiresBypass()){
      setByPassDialogOpen(true);
      return;
    }
    try {setShowLoader(true);
      const lockInventoryRequestModel = {
        UserId: userId,
        isByPass: isByPassConfirm
      };
      const data = await inventoryService.lockInventoryAsync(
        inventoryId, 
        lockInventoryRequestModel
      );
      handleMenuClose();
      setIsLocked(true);
      setSeverity("success");
      setSnackbarMessage(data.message);
      setOpenSnackbar(true);
      setTimeout(() => {
        handleCloseSnackbar();
      }, 5000);
      await fetchRecentlyLockedInventory();
    } catch (error: any) {
   setDialogConfig({ ...dialogConfig, open: false })
      const errorMessage =
        error.response?.data?.message || "Error locking inventory.";
      handleMenuClose();
      setSeverity("error");
      setSnackbarMessage(errorMessage);
      setOpenSnackbar(true);
      setTimeout(() => {
        handleCloseSnackbar();
      }, 5000);
      console.error("Error locking inventory:", errorMessage);
    }finally {
      setShowLoader(false);
    }
  };
  const handleUnLockInventory = async () => {
    if (!activeInventory || !inventoryId) return;
    try {setShowLoader(true);
      const unlockInventoryRequestModel = {
        UserId: userId,
      };
     const data = await inventoryService.unLockInventoryAsync(
        inventoryId,
        unlockInventoryRequestModel
      );
      handleMenuClose();
      setSeverity("success");
      setSnackbarMessage(data.message);
      // const activeInv = await inventoryService.getActiveInventorySessionAsync();
      // if(activeInventory && activeInv.inventory){
      //   setIsTagCompleted(activeInv.inventory.isTagCompleted);
      //   setIsLocked(activeInv.inventory.isLocked);
      // }
      fetchActiveInventory();
      setOpenSnackbar(true);
      setTimeout(() => {
        handleCloseSnackbar();
      }, 5000);
    } catch (error: any) {
     setDialogConfig({ ...dialogConfig, open: false })
      const errorMessage =
        error.response?.data?.message || "Error unlocking inventory.";
      handleMenuClose();
      setSeverity("error");
      setSnackbarMessage(errorMessage);
      setOpenSnackbar(true);
      setTimeout(() => {
        handleCloseSnackbar();
      }, 5000);
      console.error("Error unlocking inventory:", errorMessage);
    }finally {
      setShowLoader(false);
    }
  };
  const handleByPassInventory = () => {
     setIsByPassConfirm(true);
   };
   useEffect(() => {
     if(isByPassConfirm){
     setByPassDialogOpen(false);
     handleLock(true);
     }
   }, [isByPassConfirm]);
   const requiresBypass = () =>
   adminExceptionProps?.unacceptedExceptions && !isByPassConfirm;
 const openLockDialog = (isOpen: boolean) => {
   if (!isOpen) {
     handleLock(false);
     setIsByPassConfirm(false);
     return;
   }
 
   if (requiresBypass()) {
     setByPassDialogOpen(true);
   } else {
     handleLock(true);
   }
 };
 const handleAssignAuditors = () => {
  inventoryUtils.assignAuditors(
    inventoryId,
    userId,
    setSnackbarMessage,
    setSeverity,
    setOpenSnackbar,
    setShowLoader,
    handleMenuClose,
    handleCloseSnackbar
  );
}; 
const handleDiscardInventory= () =>{
   inventoryUtils.discardInventory(
    inventoryId,
    userId,
    setSnackbarMessage,
    setSeverity,
    setOpenSnackbar,
    setShowLoader,
    handleMenuClose,
    handleCloseSnackbar,
  );
   navigate('/ICM');
}
  const handleLock=(isOpen:boolean)=>{
    if(isOpen === false){
      setIsByPassConfirm(false);
    }
    setDialogConfig({
      open:isOpen,
      title: "Are you sure you want to lock this inventory?",
      message: "By locking it, no one from your company will be able to edit the inventory.",
      onConfirm: handleLockInventory,
    });
  }
const handleUnLock=()=>{
    setDialogConfig({
      open: true,
  title: "Are you sure you want to unlock this inventory?",
  message: "By unlocking, you will be able to edit tags under the inventory.",
  onConfirm: handleUnLockInventory,});}
  const handleDiscard=()=>{
    setDialogConfig({
      open: true,
  title: "Are you sure you want to discard this inventory?",
  message: "This action is irreversible, and the inventory will no longer be available for use.",
  onConfirm: handleDiscardInventory,});}
  return (
    <div className="content_container">
      {hasInventorySession ? (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <h3 className={styles.page_title}>
                {activeInventory ? activeInventory.name : ""}
                {isLocked && (
                  <IconButton edge="end" size="small">
                    <Icon path={mdiLockOutline} size={1} />
                  </IconButton>
                )}
                <IconButton onClick={handleMenuOpen}>
                  <Icon path={mdiDotsVertical} size={1} />
                </IconButton>
                <Menu
                  anchorEl={inventoryMenuEl}
                  open={Boolean(inventoryMenuEl)}
                  onClose={handleMenuClose}
                  keepMounted
                  className={styles.inventoryDropMenu}
                  slotProps={{
                    paper: {
                      sx: {
                        width: "250px",
                      },
                    },
                  }}
                >
                  { !isLocked && (
                  <Tooltip
                    title={
                      (!isTagCompleted && activeInventory != null) || isLocked
                        ? "The inventory is locked, you cannot edit this inventory."
                        : ""
                    }
                    placement="top"
                    arrow
                    disableHoverListener={!isLocked} // Show tooltip only when the menu item is disabled
                  >
                    <div>
                    <MenuItem
                    onClick={() => {
                              handleMenuClose();
                              openLockDialog(true)
                            }}
                    disabled={
                              (!isTagCompleted && activeInventory != null) ||
                              isLocked
                            }>
  <Box display="flex" alignItems="center" gap={1}>
    Lock Inventory
    {
        adminException && adminException.isException 
        && (<CustomTooltip title={adminException.tooltipTitle} tooltipMessage={adminException.tooltipLockMessage}>
          <IconButton className={styles.exceptionIcon} sx={{ p: 0 }}>
            <Icon path={mdiAlertCircleOutline} size={1} color={adminExceptionProps?.unacceptedExceptions?.trim()? "red" : "grey"} />
          </IconButton>
        </CustomTooltip> 
      )}
  </Box>
  <Icon path={mdiChevronRight} size={0.9} />
</MenuItem>
                    </div>
                  </Tooltip>
                  )}
                  {(activeInventory == null || isLocked) && (
                  <MenuItem
                        onClick={() => { handleMenuClose(); handleUnLock()}}
                      >
                        UnLock Inventory{" "}
                        <Icon path={mdiChevronRight} size={0.9} />
                      </MenuItem>
                    )}
                  <MenuItem onClick={handleNewSession} disabled={!isLocked}>
                    Create a new session{" "}
                    <Icon path={mdiChevronRight} size={0.9} />
                  </MenuItem>
                   <MenuItem onClick={handleDiscard}disabled={isLocked} >
                                     Discard Inventory{" "}
                    <Icon path={mdiChevronRight} size={0.9} />
                  </MenuItem>
                   <MenuItem onClick={handleAssignAuditors}disabled={isLocked} >
                                      Assign Auditor{" "}
                    <Icon path={mdiChevronRight} size={0.9} />
                  </MenuItem>
                </Menu>
              </h3>
            </Grid>
          </Grid>
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={openSnackbar}
            autoHideDuration={3000}
            onClose={handleCloseSnackbar}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={severity}
              icon={getIcon()}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          {emptyState ? (
            <EmptyDashboard
              title="No Tag Added"
              subtitle="Currently, no tag is added at the moment."
            />
          ) : (
            <InventoryDetails
              updateEmptyState={(isEmpty: boolean) => {
              setEmptyState(isEmpty);
              }}
              recentlyLockedInventory={recentlyLockedInventory}
              isLocked={isLocked}
            />
          )}
          <Suspense fallback={<div>Loading...</div>}>
            <CreateCopyInventoryDialog
              open={createCopyInventoryOpen}
              onClose={onCreateCopyInventoryClose}
              onSuccess={handleDialogSuccess}
              toggleLoader={toggleLoader}
            />
           <ConfirmationDialog
             count={null}
             title={dialogConfig.title}
             message={dialogConfig.message}
             open={dialogConfig.open}
             onClose={() => {setIsByPassConfirm(false);setDialogConfig({ ...dialogConfig, open: false })}}
            onConfirm={() => {
               dialogConfig.onConfirm?.();
               setDialogConfig({ ...dialogConfig, open: false });
             }}
                      />
                      {byPassDialogOpen &&
                          (<ConfirmationDialog
                              open={byPassDialogOpen}
                              title="Bypass Exceptions and Lock Inventory?"
                              message="There are outstanding exceptions, but as an admin, you can choose to proceed. Locking the inventory will override these checks. Are you sure you want to continue?"
                              onClose={() => setByPassDialogOpen(false)}
                              onConfirm={() => handleByPassInventory()}
                              isDisplayIcon={true}
                          />)
                      }
          </Suspense>
        </>
      ) : (
        <>
          <h3 className={styles.page_title}>Welcome {userName}</h3>
          <p>At the moment, <strong>No Inventory Session</strong> is active.</p>
        </>
      )} {showLoader && <Loader/>}
    </div>
  );
};

export default Inventory;
